// Workshop — admin-only multi-org batch provisioning. Pick a pack +
// volume, pick the orgs (each is an existing OrgConnection row),
// fan out one provisioning job per org. Mirrors POST /api/jobs/batch
// in the production backend.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { adminApi } from "@/lib/api/admin";
import { api } from "@/lib/api";
import { useUiStore } from "@/lib/store/uiStore";

const VOLUMES = ["small", "medium", "large", "xlarge"];

export function Workshop(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const showToast = useUiStore((s) => s.showToast);

  const [packId, setPackId] = useState<string>("");
  const [volume, setVolume] = useState("medium");
  const [includeSales, setIncludeSales] = useState(true);
  const [includeService, setIncludeService] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<{ job_id: string; org_id: string }[] | null>(null);

  useEffect(() => { document.title = "Workshop · Admin"; }, []);

  const { data: orgs = [] } = useQuery({
    queryKey: ["admin", "orgs"],
    queryFn: adminApi.orgConnections,
    enabled: auth.isAdmin,
  });
  const { data: packs = [] } = useQuery({
    queryKey: ["packs"],
    queryFn: api.listPacks,
  });

  const eligiblePacks = useMemo(() => packs.filter((p) => p.type === "official"), [packs]);

  const batch = useMutation({
    mutationFn: () =>
      adminApi.batchProvision({
        org_connection_ids: Array.from(selected),
        industry_pack_id: packId,
        volume_preset: volume,
        include_sales_cloud: includeSales,
        include_service_cloud: includeService,
      }),
    onSuccess: (res) => {
      setResults(res.jobs);
      showToast(`Queued ${res.jobs.length} jobs`);
    },
    onError: (e) => showToast(`Batch failed: ${(e as Error).message}`),
  });

  if (auth.loading) return <div style={{ padding: 48 }}>Loading…</div>;
  if (!auth.isAdmin) {
    return (
      <div style={{ padding: 60, maxWidth: 480, margin: "0 auto", textAlign: "center" }}>
        <div className="card" style={{ padding: 32 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>Admin access required</h2>
          <button className="btn primary" style={{ marginTop: 14 }} onClick={() => navigate("/team-login")}>
            Q Branch sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="hero-gradient" style={{ padding: "32px 32px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="label" style={{ marginBottom: 6 }}>Workshop</div>
          <h1 style={{ margin: 0, fontSize: 28, color: "var(--eb-15)", fontWeight: 700 }}>
            Multi-org batch provision
          </h1>
          <p style={{ color: "var(--ink-2)", marginTop: 6, fontSize: 13, maxWidth: 720 }}>
            Pick a pack, select orgs, and queue one provisioning job per org. Useful for workshop / training cohorts.
          </p>
        </div>
      </section>

      <section style={{ padding: "28px 32px", background: "var(--cb-98)", minHeight: "60vh" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 320px", gap: 16 }}>
          <div className="card" style={{ padding: 20 }}>
            <div className="label" style={{ marginBottom: 8 }}>Orgs ({orgs?.length ?? 0})</div>
            {orgs?.length === 0 && (
              <div style={{ color: "var(--ink-3)", fontSize: 12, padding: 24, textAlign: "center" }}>
                No org connections yet. Have users sign in via Connect org first.
              </div>
            )}
            <div style={{ display: "grid", gap: 6, maxHeight: 460, overflowY: "auto" }} className="thin-scroll">
              {(orgs ?? []).map((o) => {
                const checked = selected.has(o.id);
                return (
                  <label
                    key={o.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      gap: 10,
                      alignItems: "center",
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: checked ? "var(--cb-95)" : "white",
                      border: `1px solid ${checked ? "var(--eb-50)" : "var(--bd)"}`,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(o.id); else next.delete(o.id);
                        setSelected(next);
                      }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{o.org_name ?? "(unknown)"}</div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                        {o.user_email ?? ""} {o.instance_url ? `· ${trimHost(o.instance_url)}` : ""}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button className="btn ghost sm" onClick={() => setSelected(new Set((orgs ?? []).map((o) => o.id)))}>
                Select all
              </button>
              <button className="btn ghost sm" onClick={() => setSelected(new Set())}>
                Clear
              </button>
            </div>
          </div>

          <div className="card" style={{ padding: 20, alignSelf: "start" }}>
            <div className="label" style={{ marginBottom: 8 }}>Pack</div>
            <select value={packId} onChange={(e) => setPackId(e.target.value)} style={fieldInput}>
              <option value="">— choose pack —</option>
              {eligiblePacks.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            <div className="label" style={{ marginTop: 14, marginBottom: 8 }}>Volume</div>
            <select value={volume} onChange={(e) => setVolume(e.target.value)} style={fieldInput}>
              {VOLUMES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>

            <div className="label" style={{ marginTop: 14, marginBottom: 8 }}>Clouds</div>
            <label style={checkRow}>
              <input type="checkbox" checked={includeSales} onChange={(e) => setIncludeSales(e.target.checked)} />
              Sales Cloud
            </label>
            <label style={checkRow}>
              <input type="checkbox" checked={includeService} onChange={(e) => setIncludeService(e.target.checked)} />
              Service Cloud
            </label>

            <button
              className="btn primary"
              style={{ width: "100%", marginTop: 16 }}
              onClick={() => batch.mutate()}
              disabled={selected.size === 0 || !packId || batch.isPending || selected.size > 20}
            >
              {batch.isPending ? "Queuing…" : `Provision ${selected.size} org${selected.size === 1 ? "" : "s"}`}
            </button>
            {selected.size > 20 && (
              <div style={{ marginTop: 8, color: "var(--pink)", fontSize: 11 }}>
                Max 20 orgs per batch. Trim selection.
              </div>
            )}
            {results && (
              <div style={{ marginTop: 14, padding: 10, background: "#e8f5ee", border: "1px solid #b3dfc1", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--teal)" }}>
                  Queued {results.length} jobs
                </div>
                <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
                  {results.map((r) => (
                    <button
                      key={r.job_id}
                      onClick={() => navigate(`/monitor/${r.job_id}`)}
                      style={{
                        textAlign: "left",
                        background: "transparent",
                        border: 0,
                        color: "var(--eb-50)",
                        fontSize: 11,
                        cursor: "pointer",
                      }}
                    >
                      → {r.job_id}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function trimHost(url?: string): string {
  if (!url) return "";
  try { return new URL(url).host; } catch { return url; }
}

const fieldInput: React.CSSProperties = {
  width: "100%",
  background: "white",
  border: "1px solid var(--bd)",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const checkRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 0",
  fontSize: 12,
  color: "var(--ink-2)",
  cursor: "pointer",
};
